import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import LinkIcon from '@mui/icons-material/Link';
import { useSearchParams } from 'react-router-dom';
import PageWithTitle from '../../components/atom/PageWithTitle';
import { duration } from '../../components/parking/eventItems';
import {
    VISIT_CLS,
    VISIT_END,
    VISIT_MAX_DAYS,
    dayOf,
    gateLabel,
    imageUrl,
    searchVisits,
} from '../../helpers/parkingEvents';
import VisitDrawer, { DRAWER_WIDTH } from './VisitDrawer';
import DiffPlate, { plateSx } from './DiffPlate';
import LinkDialog from './LinkDialog';
import QueueTab from './QueueTab';
import Reasons from './Reasons';

const LIMIT = 300;
const DAY = 864e5;

// gates per area (as the backend's area filter)
const AREAS = Object.freeze({
    garage: {
        label: 'Mélygarázs',
        gates: ['Entry_BE1', 'Entry_BE2', 'Exit_KI1', 'Exit_KI2', 'Gate_KapuMG'],
    },
    surface: {
        label: 'Felszíni',
        gates: ['Entry_BE3', 'Entry_BE4', 'Exit_KI3', 'Exit_KI4', 'Exit_KI5'],
    },
});

const today = () => dayOf(Date.now());
const daysBack = (n) => dayOf(Date.now() - n * DAY);
const PRESETS = [
    { label: 'Ma', from: () => today(), to: () => today() },
    { label: 'Tegnap', from: () => daysBack(1), to: () => daysBack(1) },
    { label: '7 nap', from: () => daysBack(6), to: () => today() },
    { label: '30 nap', from: () => daysBack(29), to: () => today() },
    { label: `${VISIT_MAX_DAYS} nap`, from: () => daysBack(VISIT_MAX_DAYS - 1), to: () => today() },
];

const FILTER_KEYS = ['plate', 'from', 'to', 'end', 'area', 'gate', 'cls'];
const huf = (n) => `${new Intl.NumberFormat('hu-HU').format(n || 0)} Ft`;
const normPlate = (s) =>
    String(s || '')
        .toUpperCase()
        .replace(/[^0-9A-Z]/g, '');

function VisitRows({ rows, selectedId, onSelect, similar, onLink }) {
    return rows.map(({ visit: v, diff }) => {
        const end = VISIT_END[v.end] || { label: v.end, color: 'default' };
        let endText = v.endTime ? v.endTime.slice(5, 16) : '–';
        if (v.out) endText = `${v.out.time.slice(5, 16)} · ${gateLabel(v.out.gate)}`;
        else if (v.release)
            endText = `${v.release.time.slice(5, 16)} · ${
                v.release.type === 'opening' ? 'kézi nyitás' : '1x jegy'
            } · ${gateLabel(v.release.gate)}`;
        return (
            <TableRow
                key={v.id}
                hover
                selected={v.id === selectedId}
                onClick={() => onSelect(v.id)}
                sx={{ cursor: 'pointer', opacity: similar ? 0.9 : 1 }}
            >
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{v.in.time.slice(0, 16)}</TableCell>
                <TableCell
                    sx={{
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        fontSize: 15,
                        letterSpacing: 0.5,
                    }}
                >
                    <DiffPlate plate={v.plate || '–'} diff={diff} />
                    {v.plates && v.plates.length > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            + {v.plates.join(', ')}
                        </Typography>
                    )}
                    {v.card && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            kártya {v.card}
                        </Typography>
                    )}
                </TableCell>
                <TableCell>{VISIT_CLS[v.cls] || v.cls}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{gateLabel(v.in.gate)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{endText}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {v.durMin != null ? duration(v.durMin * 60e3) : '–'}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{v.paid ? huf(v.paid) : '–'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{v.fee ? huf(v.fee.fee) : '–'}</TableCell>
                <TableCell>{v.rejections || ''}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Chip size="small" color={end.color} label={end.label} />
                    {v.linkCount > 0 && (
                        <Tooltip title={`${v.linkCount} kapcsolat`}>
                            <LinkIcon
                                fontSize="small"
                                color="primary"
                                sx={{ ml: 0.5, verticalAlign: 'middle' }}
                            />
                        </Tooltip>
                    )}
                </TableCell>
                {onLink && (
                    <TableCell padding="none" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Összekapcsolás a keresett rendszámmal">
                            <IconButton size="small" onClick={() => onLink(v, diff)}>
                                <LinkIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </TableCell>
                )}
            </TableRow>
        );
    });
}

function ResultTable({ rows, selectedId, onSelect, similar, onLink }) {
    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Behajtás</TableCell>
                    <TableCell>Rendszám / jegy</TableCell>
                    <TableCell>Típus</TableCell>
                    <TableCell>Be</TableCell>
                    <TableCell>Ki / vége</TableCell>
                    <TableCell>Idő</TableCell>
                    <TableCell>Fizetve</TableCell>
                    <TableCell>Díj (lekérdezve)</TableCell>
                    <TableCell>Elut.</TableCell>
                    <TableCell>Állapot</TableCell>
                    {onLink && <TableCell padding="none" />}
                </TableRow>
            </TableHead>
            <TableBody>
                <VisitRows
                    rows={rows}
                    selectedId={selectedId}
                    onSelect={onSelect}
                    similar={similar}
                    onLink={onLink}
                />
            </TableBody>
        </Table>
    );
}

// the customer's plate has no open parking: the open ones one character off (9.1, at the cashier)
function SuggestionCard({ suggestions, onOpen, onLink }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, borderColor: 'warning.main', borderWidth: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Javaslat – nincs nyitott parkolás erre a rendszámra, ezek 1 karakterrel térnek el
            </Typography>
            {suggestions.map((c) => {
                const v = c.visit;
                const url = imageUrl(v.in);
                return (
                    <Stack
                        key={v.id}
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ sm: 'center' }}
                        sx={{ py: 1, borderTop: 1, borderColor: 'divider' }}
                    >
                        <Box
                            sx={{
                                width: 160,
                                aspectRatio: '16 / 9',
                                bgcolor: url ? '#000' : 'action.hover',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            {url ? (
                                <Box
                                    component="img"
                                    src={url}
                                    alt=""
                                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                            ) : (
                                <Typography variant="caption" color="text.secondary">
                                    nincs kép
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ minWidth: 150 }}>
                            <Typography sx={{ ...plateSx, fontSize: 20 }}>
                                <DiffPlate plate={v.plate} diff={c.diff} />
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {v.in.time.slice(5, 16)} · {gateLabel(v.in.gate)}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Reasons reasons={c.reasons} dense />
                        </Box>
                        <Stack spacing={0.5}>
                            <Button
                                variant="contained"
                                onClick={() => onLink(v, c.diff, c.reasons)}
                            >
                                Összekapcsolás…
                            </Button>
                            <Button size="small" onClick={() => onOpen(v.id)}>
                                Megnyitás
                            </Button>
                        </Stack>
                    </Stack>
                );
            })}
        </Paper>
    );
}

/**
 * Parking visits (entry → exit of one parking record) from the backend's visit files: plate search with the similar
 * plates apart, filters for time / area / gate / status / type, the visit's timeline in a drawer.
 * Not in the menu yet: /visits
 */
const VisitsPage = () => {
    const [params, setParams] = useSearchParams();
    const get = (k) => params.get(k) || '';
    const filters = {
        plate: get('plate'),
        from: get('from') || daysBack(6),
        to: get('to') || today(),
        end: get('end'),
        area: get('area'),
        gate: get('gate'),
        cls: get('cls'),
    };
    const selectedId = get('id');
    const [plateInput, setPlateInput] = useState(filters.plate);
    const [state, setState] = useState({ loading: false, data: null, error: null });
    const tab = get('tab') === 'todo' ? 'todo' : 'search';
    // bumped by every decision: the lists, the queue and the drawer reload
    const [version, setVersion] = useState(0);
    const [dialog, setDialog] = useState(null); // {visit, item, diff, reasons}

    const update = (patch) => {
        const next = new URLSearchParams(params);
        Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
        setParams(next, { replace: true });
    };

    // typing: search after a short pause
    useEffect(() => {
        const q = normPlate(plateInput);
        if (q === filters.plate) return undefined;
        const t = setTimeout(() => update({ plate: q }), 400);
        return () => clearTimeout(t);
    }, [plateInput]);

    const key = FILTER_KEYS.map((k) => filters[k]).join('|');
    useEffect(() => {
        let stop = false;
        setState((s) => ({ ...s, loading: true, error: null }));
        const req = { limit: LIMIT };
        FILTER_KEYS.forEach((k) => {
            if (filters[k]) req[k] = filters[k];
        });
        searchVisits(req)
            .then((data) => !stop && setState({ loading: false, data, error: null }))
            .catch((error) => !stop && setState({ loading: false, data: null, error }));
        return () => {
            stop = true;
        };
    }, [key, version]);

    // link a parking with the searched plate (the customer's plate as a variant of it)
    const linkPlate = (visit, diff, reasons = []) =>
        setDialog({
            visit,
            diff,
            reasons,
            item: {
                type: 'plate',
                ref: filters.plate,
                plate: filters.plate,
                title: 'Keresett rendszám',
                lines: ['az ügyfél / a kamera szerinti rendszám'],
            },
        });
    const changed = () => setVersion((x) => x + 1);

    const rangeDays = Math.round((new Date(filters.to) - new Date(filters.from)) / DAY) + 1;
    const gates = filters.area
        ? AREAS[filters.area].gates
        : [...AREAS.garage.gates, ...AREAS.surface.gates];
    const { data } = state;
    const exact = useMemo(() => (data ? data.exact.map((visit) => ({ visit })) : []), [data]);
    const similar = data ? data.similar : [];

    // filters that narrow the list, shown as removable chips
    const active = [
        filters.plate && { k: 'plate', label: `Rendszám: ${filters.plate}` },
        filters.end && { k: 'end', label: `Állapot: ${VISIT_END[filters.end]?.label}` },
        filters.area && { k: 'area', label: `Terület: ${AREAS[filters.area]?.label}` },
        filters.gate && { k: 'gate', label: `Kapu: ${gateLabel(filters.gate)}` },
        filters.cls && { k: 'cls', label: `Típus: ${VISIT_CLS[filters.cls]}` },
    ].filter(Boolean);

    // "új ügyfél": everything back to the defaults
    const reset = () => {
        setPlateInput('');
        setParams(new URLSearchParams(), { replace: true });
    };

    return (
        <Box sx={{ mr: selectedId ? { sm: `${DRAWER_WIDTH}px` } : 0, transition: 'margin 0.2s' }}>
            <PageWithTitle
                title="Látogatások"
                headline={
                    <Button
                        variant="outlined"
                        startIcon={<PersonAddAltIcon />}
                        onClick={reset}
                        sx={{ mt: 1 }}
                    >
                        Új ügyfél
                    </Button>
                }
            >
                <Grid item xs={12}>
                    <Tabs
                        value={tab}
                        onChange={(_, t) => update({ tab: t === 'todo' ? 'todo' : '' })}
                    >
                        <Tab value="search" label="Keresés" />
                        <Tab value="todo" label="Teendők" />
                    </Tabs>
                </Grid>
                {tab === 'todo' && (
                    <QueueTab
                        version={version}
                        onChanged={changed}
                        onOpen={(id) => update({ id })}
                    />
                )}
                {tab === 'search' && (
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={1.5}>
                                <Stack
                                    direction="row"
                                    spacing={1.5}
                                    flexWrap="wrap"
                                    useFlexGap
                                    alignItems="center"
                                >
                                    <TextField
                                        size="small"
                                        autoFocus
                                        placeholder="Rendszám / jegy / bérletkártya"
                                        value={plateInput}
                                        onChange={(e) =>
                                            setPlateInput(e.target.value.toUpperCase())
                                        }
                                        onKeyDown={(e) =>
                                            e.key === 'Enter' &&
                                            update({ plate: normPlate(plateInput) })
                                        }
                                        sx={{ minWidth: 280 }}
                                        InputProps={{
                                            sx: { fontFamily: 'monospace', fontWeight: 700 },
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        size="small"
                                        type="date"
                                        label="Behajtás -tól"
                                        value={filters.from}
                                        onChange={(e) =>
                                            e.target.value && update({ from: e.target.value })
                                        }
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <TextField
                                        size="small"
                                        type="date"
                                        label="-ig"
                                        value={filters.to}
                                        onChange={(e) =>
                                            e.target.value && update({ to: e.target.value })
                                        }
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <Stack direction="row" spacing={0.5}>
                                        {PRESETS.map((p) => {
                                            const on =
                                                filters.from === p.from() && filters.to === p.to();
                                            return (
                                                <Chip
                                                    key={p.label}
                                                    label={p.label}
                                                    size="small"
                                                    color={on ? 'primary' : 'default'}
                                                    variant={on ? 'filled' : 'outlined'}
                                                    onClick={() =>
                                                        update({ from: p.from(), to: p.to() })
                                                    }
                                                />
                                            );
                                        })}
                                    </Stack>
                                    {state.loading && <CircularProgress size={20} />}
                                </Stack>
                                <Stack
                                    direction="row"
                                    spacing={1.5}
                                    flexWrap="wrap"
                                    useFlexGap
                                    alignItems="center"
                                >
                                    <ToggleButtonGroup
                                        size="small"
                                        exclusive
                                        value={filters.area}
                                        onChange={(_, v) =>
                                            update({
                                                area: v,
                                                gate:
                                                    v &&
                                                    filters.gate &&
                                                    !AREAS[v].gates.includes(filters.gate)
                                                        ? ''
                                                        : filters.gate,
                                            })
                                        }
                                    >
                                        <ToggleButton value="">Teljes terület</ToggleButton>
                                        <ToggleButton value="garage">Mélygarázs</ToggleButton>
                                        <ToggleButton value="surface">Felszíni</ToggleButton>
                                    </ToggleButtonGroup>
                                    <TextField
                                        select
                                        size="small"
                                        label="Kapu"
                                        value={filters.gate}
                                        onChange={(e) => update({ gate: e.target.value })}
                                        sx={{ minWidth: 170 }}
                                    >
                                        <MenuItem value="">Mind</MenuItem>
                                        {gates.map((g) => (
                                            <MenuItem key={g} value={g}>
                                                {gateLabel(g)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        select
                                        size="small"
                                        label="Típus"
                                        value={filters.cls}
                                        onChange={(e) => update({ cls: e.target.value })}
                                        sx={{ minWidth: 150 }}
                                    >
                                        <MenuItem value="">Mind</MenuItem>
                                        {Object.entries(VISIT_CLS).map(([k, label]) => (
                                            <MenuItem key={k} value={k}>
                                                {label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Stack>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    flexWrap="wrap"
                                    useFlexGap
                                    alignItems="center"
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        Állapot:
                                    </Typography>
                                    {Object.entries(VISIT_END).map(([k, e]) => (
                                        <Chip
                                            key={k}
                                            size="small"
                                            color={e.color}
                                            variant={filters.end === k ? 'filled' : 'outlined'}
                                            label={`${e.label}${
                                                data ? ` · ${data.counts[k]}` : ''
                                            }`}
                                            onClick={() =>
                                                update({ end: filters.end === k ? '' : k })
                                            }
                                        />
                                    ))}
                                </Stack>
                                {active.length > 0 && (
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        flexWrap="wrap"
                                        useFlexGap
                                        alignItems="center"
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            Aktív szűrők:
                                        </Typography>
                                        {active.map((a) => (
                                            <Chip
                                                key={a.k}
                                                size="small"
                                                label={a.label}
                                                onDelete={() => {
                                                    if (a.k === 'plate') setPlateInput('');
                                                    update({ [a.k]: '' });
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                )}
                            </Stack>
                        </Paper>
                    </Grid>
                )}

                {tab === 'search' && state.error && (
                    <Grid item xs={12}>
                        <Alert severity="error">
                            {state.error.response?.body?.error || state.error.message}
                        </Alert>
                    </Grid>
                )}
                {tab === 'search' && data && data.linksError && (
                    <Grid item xs={12}>
                        <Alert severity="warning">
                            A kapcsolatok nem olvashatók: {data.linksError}
                        </Alert>
                    </Grid>
                )}
                {tab === 'search' && data && data.suggestions && data.suggestions.length > 0 && (
                    <Grid item xs={12}>
                        <SuggestionCard
                            suggestions={data.suggestions}
                            onOpen={(id) => update({ id })}
                            onLink={linkPlate}
                        />
                    </Grid>
                )}
                {tab === 'search' && data && data.missingDays.length > 0 && (
                    <Grid item xs={12}>
                        <Alert severity="info">
                            {data.missingDays.length} nap a {rangeDays}-ból nincs (még) feldolgozva
                            {data.missingDays.length <= 5 ? `: ${data.missingDays.join(', ')}` : ''}
                            .
                        </Alert>
                    </Grid>
                )}
                {tab === 'search' && data && data.truncated && (
                    <Grid item xs={12}>
                        <Alert severity="warning">
                            Csak az első {LIMIT} találat látszik – szűkítsd az időszakot vagy a
                            szűrőket.
                        </Alert>
                    </Grid>
                )}

                {tab === 'search' && data && (
                    <Grid item xs={12}>
                        <Paper variant="outlined">
                            <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5 }}>
                                {filters.plate ? `Pontos egyezés: ${filters.plate}` : 'Látogatások'}{' '}
                                · {exact.length}
                            </Typography>
                            <ResultTable
                                rows={exact}
                                selectedId={selectedId}
                                onSelect={(id) => update({ id })}
                            />
                            {!exact.length && (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                    Nincs találat.
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                )}
                {tab === 'search' && data && filters.plate && (
                    <Grid item xs={12}>
                        <Paper variant="outlined">
                            <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5 }}>
                                Hasonló rendszámok (1 karakter eltérés) · {similar.length}
                            </Typography>
                            <ResultTable
                                rows={similar}
                                selectedId={selectedId}
                                onSelect={(id) => update({ id })}
                                similar
                                onLink={(v, diff) => {
                                    const sug = (data.suggestions || []).find(
                                        (c) => c.visit.id === v.id
                                    );
                                    linkPlate(v, diff, sug ? sug.reasons : []);
                                }}
                            />
                            {!similar.length && (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                    Nincs hasonló rendszám az időszakban.
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                )}
            </PageWithTitle>

            <VisitDrawer
                id={selectedId}
                version={version}
                onChanged={changed}
                onOpen={(id) => update({ id })}
                onClose={() => update({ id: '' })}
            />
            <LinkDialog
                open={!!dialog}
                {...(dialog || {})}
                onClose={() => setDialog(null)}
                onDone={() => {
                    setDialog(null);
                    changed();
                }}
            />
        </Box>
    );
};

export default VisitsPage;
